// sales-page/src/pages/partnerUpsellMarkup.js
// Builds the founding-partner upsell page markup in the same "Google Doc sales
// letter" visual language as SalesLifetime.html/css (reused via SalesLifetime.css)
// -- same classes, same Arial-based type system, same bordered-box aesthetic --
// so the upsell page reads as a continuation of the main sales page, not a
// different product. Content and structure follow the source doc:
// trafiktrak-partner-badge-upsell (Google Doc) -- including its real 2-column
// table layout (text cell + image cell, alternating sides), not a stacked
// image-then-paragraph block.

const offerBox = (n) => `
  <div class="offer-box-grid">
    <div class="offer-left-col">
      <div class="offer-act-fast">ACT FAST &ndash; This $100 Discount Is Only Available On This Page. It Will Not Appear Again.</div>
      <div class="offer-regular-price">Regular Price: <s>$297</s></div>
      <div class="offer-launch-price">Today Only: $197 (One-Time Payment)</div>
      <a href="#offer" class="doc-cta-button founder-cta-btn">&lt; &#127942; YES &mdash; ADD MY FOUNDING PARTNER POSITION &gt;</a>
      <div class="offer-guarantee-note">Charged to your saved card. No card re-entry needed.</div>
      <div style="display:flex; align-items:center; justify-content:center; flex-wrap:wrap; gap:10px; margin-bottom:12px;">
        <span style="display:inline-flex; align-items:center; justify-content:center; background:#FFFFFF; border:1.5px solid #D1D5DB; border-radius:6px; padding:6px 12px; font-weight:900; font-style:italic; font-size:16px; color:#1A1F71; letter-spacing:-0.5px;">VISA</span>
        <span style="display:inline-flex; align-items:center; justify-content:center; background:#2E77BC; border-radius:6px; padding:7px 12px; font-weight:800; font-size:14px; color:#FFFFFF;">AMEX</span>
        <span style="display:inline-flex; align-items:center; justify-content:center; background:#FFFFFF; border:1.5px solid #D1D5DB; border-radius:6px; padding:7px 12px; font-weight:800; font-size:14px; color:#111111;">DISC<span style="color:#F58220;">&#9679;</span>VER</span>
        <span style="display:inline-flex; align-items:center; gap:-6px; background:#16171A; border-radius:6px; padding:7px 14px;">
          <span style="width:16px; height:16px; border-radius:50%; background:#EB001B; display:inline-block; margin-right:-6px;"></span>
          <span style="width:16px; height:16px; border-radius:50%; background:#F79E1B; opacity:0.9; display:inline-block; margin-right:6px;"></span>
          <span style="font-weight:800; font-size:13px; color:#FFFFFF;">Mastercard</span>
        </span>
        <img src="/images/guarantee-badge.png" alt="30 Day Money Back Guarantee" style="height:52px; width:52px; vertical-align:middle;">
        <span style="font-weight:800; font-size:15px; color:#111111; text-align:left; line-height:1.2;">30 Days<br>Money Back<br>Guarantee</span>
      </div>
      <div class="offer-timer-warning">&#9203; Only <span class="spots-remaining-n">${n}</span> of 50 founding spots remaining</div>
    </div>
    <div class="offer-right-col">
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div><strong>50% commission</strong> on every referral &mdash; permanently locked in for founding partners</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Your name, photo &amp; website on the TrafikTrak public landing page forever</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Private founding partner community &mdash; 50 people, not thousands</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Direct voting rights on the feature roadmap</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Private access to the founding team &mdash; not a support queue</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Priority support, front of the queue, permanently</div></div>
    </div>
  </div>
`;

// A real 2-column table row: one cell text, one cell image -- matches the
// doc's actual layout. `reverse` puts the image on the left instead of right.
const zigzag = (src, alt, textHtml, reverse = false) => `
  <div class="zigzag-box${reverse ? ' zigzag-reverse' : ''}">
    <div class="zigzag-text-col">${textHtml}</div>
    <div class="zigzag-img-col" style="background-image:url('${src}');" role="img" aria-label="${alt}"></div>
  </div>
`;

export function buildPartnerUpsellHtml({ slotsRemaining, planName, planAmount, firstName, isReturningVisitor }) {
  const n = slotsRemaining !== null && slotsRemaining !== undefined ? slotsRemaining : '{N}';
  const plan = planName || 'your plan';
  const amount = planAmount || '';
  const topBarText = isReturningVisitor && firstName
    ? `WELCOME BACK, ${firstName.toUpperCase()} &mdash; YOUR SPOT MAY STILL BE AVAILABLE`
    : '&#10003; WAIT &mdash; YOUR PURCHASE IS CONFIRMED. ONE MORE THING BEFORE YOU GO.';

  return `
  <div style="background:#166534; color:#FFFFFF; font-weight:800; font-size:18px; text-align:center; padding:14px 20px;">
    ${topBarText}
  </div>

  <div class="sales-page-container">

    <div class="sales-logo-box">
      <img src="/images/app_logo_1787919665010.jpg" alt="TrafikTrak Logo" class="sales-logo-img" referrerPolicy="no-referrer">
    </div>

    <h1 class="sales-main-headline" style="font-size:36px;">Welcome To TrafikTrak</h1>

    <p style="text-align:center; color:#38761D; font-weight:800; font-size:18px; margin-bottom:16px;">
      &#10003; Your payment is confirmed. &nbsp; &#10003; Your account is live. &nbsp; &#10003; Lifetime access is yours.
    </p>

    <div class="sales-section-heading" style="font-size:28px; margin-top:0;">Before You Go To Your Dashboard &mdash; Read This First.</div>
    <p style="text-align:center; font-style:italic; color:#434343; margin-bottom:20px;">This page is shown to new TrafikTrak members only. It will not appear again.</p>

    <div class="sales-video-wrapper">
      <div class="video-play-label">&#9654; Watch This Before You Decide &#9654;</div>
      <div class="video-sub-label">60 seconds on what the founding partner position actually is.</div>
      <div class="video-frame-container" style="aspect-ratio:auto;">
        <wistia-player media-id="zzoepu4tjb" aspect="1.7777777777777777"></wistia-player>
      </div>
    </div>

    <div class="doc-boxed-section" style="background:#FFF9E6; border-color:#D97706; text-align:center;">
      <span style="font-size:22px;">&#9203;</span>
      <strong style="color:#DC2626; font-size:19px;">${n} of 50 founding spots remaining</strong>
      &mdash; first batch closes at 50.<br>
      <span style="color:#434343;">Second batch opens at $10,000 after TrafikTrak hits $1M in revenue.</span>
    </div>

    <hr class="doc-divider">

    <div class="sales-section-heading">You're Already A Member. This Is The Question Of Whether You Want To Be A Founder.</div>
    <p style="text-align:center; font-size:18px;">There's a difference between using a platform and being part of building it. 50 people &mdash; and only 50 &mdash; will be founding partners. Here's exactly what's on the table.</p>

    ${zigzag('/images/partner-founding-stake.jpg', 'Founding partners', `
      <p>You just made a smart decision. You bought a platform you will own forever &mdash; no more monthly rent, no dependency on tools you don't control. That decision pays for itself many times over, and you already know it.</p>
      <p style="font-weight:800; font-size:19px;">It's a founding stake in what TrafikTrak is becoming.</p>
      <p>And it's only available to new members, only on this page, and only while the 50 spots last.</p>
    `)}

    ${zigzag('/images/partner-member-circle.jpg', 'Founding partner circle', `
      <p>Most people who join TrafikTrak will be users. Great users &mdash; people who get real value, run better campaigns, and stop paying rent on tools they'll never own.</p>
      <p style="font-weight:800; color:#131E43;">But 50 people, and only 50, will be something more than that.</p>
      <p>They'll be the founding partners &mdash; the ones whose names are on the public landing page, the ones in the private room with the team, the ones earning 50% commission on every sale they refer, permanently.</p>
    `, true)}

    <div class="doc-blue-banner">You are being offered one of the first 50 spots right now &mdash; regular price $297, today $100 off at $197.</div>

    <hr class="doc-divider">

    <div class="sales-section-heading">The Problem With Waiting.</div>
    <div class="doc-boxed-section" style="text-align:center; font-style:italic; font-weight:700; color:#131E43;">
      "The question isn't whether this is a good deal. The question is whether you're the kind of person who acts when something is clearly in front of them &mdash; or the kind who thinks about it until the moment's passed."
    </div>

    ${zigzag('/images/partner-urgency-moment.jpg', 'Deciding before the moment passes', `
      <p>When the 50th spot is taken, this offer closes. No appeal, no "I was just about to buy," no reopening because demand was high.</p>
      <p>The second batch opens eventually at $10,000, once the revenue milestone hits. You're being offered the position before the certainty exists &mdash; that's why it's $197 today and not $10,000. Early belief deserves a different price than late confidence.</p>
    `)}

    <hr class="doc-divider">

    <div class="sales-section-heading">Everything The Founding Partner Position Includes.</div>
    <p class="sales-section-subhead">Here's exactly what you get when you add the partnership position today.</p>

    ${zigzag('/images/partner-commission-math.jpg', 'Commission earnings', `
      <div class="bonus-doc-title" style="margin-top:0;">&#128176; 50% Commission On Every Referral &mdash; Forever</div>
      <p>Add the position and you're issued a Partners Desk access code immediately &mdash; redeem it in a couple of minutes to activate your unique partner link. Once it's live, every TrafikTrak plan purchase through your link earns you 50% of the sale. Every time. No cap, no expiry, no minimum threshold.</p>
      <p>TrafikTrak Lifetime plan ($58) &rarr; <strong>$29 commission per referral</strong></p>
      <p style="color:#16A34A; font-weight:800;">7 referrals and your $197 investment is fully paid back &mdash; every referral after that is pure profit.</p>
    `, true)}

    <div class="bonus-doc-box">
      <div class="bonus-doc-title">&#127760; Your Name, Photo &amp; Website On The Public Landing Page</div>
      <div class="bonus-doc-body">Every visitor to TrafikTrak sees a dedicated founding partners section &mdash; your photo, your full name, and a clickable link to your site, displayed publicly and permanently for as long as the platform exists. Opt-in &mdash; stay private if you'd rather.</div>
    </div>
    <div class="bonus-doc-box">
      <div class="bonus-doc-title">&#128274; A Seat In The Private Founding Partner Community</div>
      <div class="bonus-doc-body">Not a public forum, not a community of thousands. A private space for the 50 founding partners and the people building the platform.</div>
    </div>

    ${zigzag('/images/partner-roadmap-vote.jpg', 'Founding partners voting on the roadmap', `
      <div class="bonus-doc-title" style="margin-top:0;">&#128499;&#65039; Direct Voting Rights On The Feature Roadmap</div>
      <p>When we decide what to build next, founding partners vote. Your priorities shape the product.</p>
      <div class="bonus-doc-title">&#128222; Private Access To The Founding Team</div>
      <p>Not a support ticket queue. Direct access to the people building the platform, for as long as you hold the position.</p>
    `)}

    ${zigzag('/images/partner-priority-support.jpg', 'Priority support, front of the queue', `
      <div class="bonus-doc-title" style="margin-top:0;">&#9889; Priority Support &mdash; Permanently</div>
      <p>Front of the queue, every time, for as long as you're a partner.</p>
      <div class="bonus-doc-title">&#128737;&#65039; 15-Day Payment Hold Before Withdrawal</div>
      <p>Every commission is held 15 days after the sale (standard refund-window protection), then it's yours to withdraw, clean.</p>
    `, true)}

    <div id="offer"></div>
    ${offerBox(n)}

    <hr class="doc-divider">

    <div class="sales-section-heading">The Investment And What It Represents.</div>

    ${zigzag('/images/partner-public-recognition.jpg', 'Public recognition as a founding partner', `
      <p>Your investment: $197. One time. No recurring charge.</p>
      <p>What you're investing in: a permanent 50% commission rate on a platform you already use, believe in, and can speak to from real experience.</p>
    `)}

    <table class="doc-table">
      <thead><tr><th class="th-challenge">Plan Sold</th><th class="th-advantage">Your 50% Commission</th></tr></thead>
      <tbody>
        <tr><td>TrafikTrak Lifetime plan ($58)</td><td>$29 per sale</td></tr>
      </tbody>
    </table>

    ${zigzag('/images/partner-few-conversations.jpg', 'A few conversations', `
      <p>Seven referrals at $29 each: $203 earned &mdash; your $197 investment returned with room to spare. Every referral after that is straight profit, permanently.</p>
      <p style="font-weight:700;">These aren't projections &mdash; they're arithmetic. The path to returning your investment is a few conversations.</p>
    `, true)}

    ${offerBox(n)}

    <hr class="doc-divider">

    <div class="sales-section-heading">Who This Position Is For.</div>

    ${zigzag('/images/partner-private-community.jpg', 'Private founding partner community', `
      <p>You're already a TrafikTrak member. You already understand the product and what it solves &mdash; which puts you in a genuinely strong position to promote it, because you can speak from real experience, not a promotional brief.</p>
      <p>You don't need a massive audience. You need the right people in your orbit and the credibility to recommend something honestly.</p>
    `)}

    <div class="doc-boxed-section">
      <div class="checklist-item"><span class="check-icon">&#9733;</span><div>An email list or newsletter audience &mdash; even a small one &mdash; of marketers, entrepreneurs, or business owners.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9733;</span><div>A social media presence in any marketing-adjacent niche.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9733;</span><div>A client base as a consultant, agency owner, or freelancer.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9733;</span><div>A community, mastermind, or network of people who run campaigns or manage affiliates.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9733;</span><div>A course, membership, or following where marketing tools are a natural conversation.</div></div>
    </div>

    <hr class="doc-divider">

    <div class="sales-section-heading">The Founding Partner Agreement &mdash; In Plain Language.</div>
    <div class="doc-boxed-section">
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Your Partners Desk access code is issued immediately after adding the position &mdash; redeem it there to activate your unique promotional link.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Once your link is active, every sale through it earns 50% commission, held 15 days, then available to withdraw.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Your public profile listing is opt-in and goes live within 48 hours of submitting your details.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Your private community and founding-team access activate immediately.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Your roadmap voting rights are active from your first community login.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Your priority support status applies to your account immediately.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>The position is tied to your account and is non-transferable.</div></div>
      <div class="checklist-item"><span class="check-icon">&#9989;</span><div>Your 50% commission rate is locked in permanently for founding partners, regardless of future changes to the standard affiliate program.</div></div>
    </div>

    <hr class="doc-divider">

    <div class="sales-section-heading">The 50-Spot Structure.</div>

    ${zigzag('/images/partner-milestone-growth.jpg', 'The $1,000,000 revenue milestone', `
      <p>We're building toward 100 founding partners in total. The first 50 spots are open now &mdash; regular price $297, today $197.</p>
      <p>The second 50 open once TrafikTrak has generated $1,000,000 in total platform revenue &mdash; priced at $10,000 each. Between now and that milestone, the founding partner group is closed. No exceptions, no partial spots.</p>
    `, true)}

    <div class="doc-blue-banner">${n} of 50 spots remaining right now.</div>

    <hr class="doc-divider">

    <div class="sales-section-heading">Frequently Asked Questions.</div>
    <div class="faq-qa-item">
      <div class="faq-q-text">"What if I can't generate any referrals?"</div>
      <div class="faq-a-text">You've already bought the platform plan, which is valuable on its own. The partnership position adds community access, voting rights, team access, priority support, and public recognition. If those are worth $197 to you without a single referral, add it. The commission is the upside, not the minimum.</div>
    </div>
    <div class="faq-qa-item">
      <div class="faq-q-text">"Will the 50% rate ever change for founding partners?"</div>
      <div class="faq-a-text">No. Founding partners lock in their rate permanently. Future changes to the affiliate program don't touch it.</div>
    </div>
    <div class="faq-qa-item">
      <div class="faq-q-text">"What does the public listing actually look like?"</div>
      <div class="faq-a-text">A dedicated section on the TrafikTrak landing page &mdash; your photo, full name, and a clickable link to your site or profile of choice. Permanent, visible, linked for as long as the platform exists.</div>
    </div>
    <div class="faq-qa-item">
      <div class="faq-q-text">"Is $100 off a real discount, or just a number to make $197 look cheap?"</div>
      <div class="faq-a-text">The regular position price is $297. New members get $100 off during this one-time post-purchase window. Once you leave this page, the discount is gone; the position (while spots last) reverts to $297.</div>
    </div>
    <div class="faq-qa-item">
      <div class="faq-q-text">"Is the $10,000 second-batch pricing real?"</div>
      <div class="faq-a-text">It's real. Once TrafikTrak hits $1,000,000 in total revenue, the second batch opens at $10,000. That's a commitment, not a marketing line. If TrafikTrak never reaches that milestone, the second batch simply never opens &mdash; your founding partner position stays exactly as described either way.</div>
    </div>

    ${offerBox(n)}

    <hr class="doc-divider">

    <div class="sales-section-heading">Secure Your Founding Partner Position Before The Spots Fill.</div>

    ${zigzag('/images/partner-closing-legacy.jpg', "You're already a member", `
      <p>You're already a member. The only question left is whether you want to be a founder.</p>
      <p style="font-weight:700;">The door's open. For exactly as many spots as remain.</p>
    `)}

    <p style="text-align:center; margin-top:20px;">
      <button type="button" id="decline-link" style="background:none; border:none; color:#666666; text-decoration:underline; font-size:16px; cursor:pointer; font-family:inherit;">No thanks &mdash; just take me to my ${plan} account</button>
    </p>

    <div class="doc-boxed-section" style="margin-top:32px;">
      <p style="margin-bottom:8px;"><strong>P.S.</strong> &mdash; Your name, your face, your website on the TrafikTrak landing page, permanently. Every person who researches TrafikTrak before buying sees that you were one of the 50 who believed in it first. That credential doesn't expire and can't be faked.</p>
      <p style="margin-bottom:0;"><strong>P.P.S.</strong> &mdash; If the only thing slowing you down is the $197: you just paid ${amount ? `$${amount}` : 'for'} the platform plan. The question isn't whether $197 is a lot of money in isolation &mdash; it's whether a permanent founding partner position, 50% commission, public recognition, community access, roadmap voting, team access, and priority support is worth $197 (a real $100 off the $297 regular price) on top of what you already paid.</p>
    </div>

    <p style="text-align:center; color:#434343; font-size:14px; margin-top:24px;">
      &#128274; Secure charge via Stripe &middot; Same card used at checkout
    </p>

    <footer style="margin-top: 48px; padding-top: 24px; border-top: 1px solid #D1D5DB; text-align: center; font-size: 13px; color: #6B7280; line-height: 1.7;">
      <div style="margin-bottom: 14px;">
        <a href="#offer" style="color: #4B5563; text-decoration: underline; margin: 0 10px; font-weight: 600;">Home</a> |
        <a href="#" data-legal="privacy" style="color: #4B5563; text-decoration: underline; margin: 0 10px; font-weight: 600;">Privacy Policy</a> |
        <a href="#" data-legal="terms" style="color: #4B5563; text-decoration: underline; margin: 0 10px; font-weight: 600;">Terms &amp; Conditions</a> |
        <a href="#" data-legal="earnings" style="color: #4B5563; text-decoration: underline; margin: 0 10px; font-weight: 600;">Earnings Disclaimer</a> |
        <a href="#" data-legal="refund" style="color: #4B5563; text-decoration: underline; margin: 0 10px; font-weight: 600;">Refund Policy</a> |
        <a href="mailto:support@trafiktrak.com" style="color: #4B5563; text-decoration: underline; margin: 0 10px; font-weight: 600;">Contact Support</a>
      </div>

      <div style="margin-bottom: 12px; font-weight: 700; color: #374151;">
        Copyright &copy; 2026 TrafikTrak. All Rights Reserved.
      </div>

      <div style="max-width: 720px; margin: 0 auto 12px; font-size: 12px; color: #9CA3AF;">
        <strong>Disclaimer:</strong> This website is not affiliated with, endorsed by, or sponsored by Meta Platforms, Inc., Google LLC, Twitter/X Corp, or any of their respective subsidiaries. All product names, logos, and brands are property of their respective owners.
      </div>

      <div style="max-width: 720px; margin: 0 auto; font-size: 12px; color: #9CA3AF;">
        <strong>Earnings Disclaimer:</strong> Commission figures shown are examples based on the stated commission rate and are not guarantees or projections of actual earnings. Your actual referral results will vary and depend on many factors including your audience, effort, and individual circumstances.
      </div>
    </footer>

  </div>

  <!-- PERSISTENT FLOATING BOTTOM URGENCY BAR -->
  <div class="sticky-footer-bar sticky-footer-compact">
    <div class="sticky-footer-inner">
      <div style="font-size: 13px; font-weight: 700;">
        &#127942; <strong>Founding Partner Offer:</strong> $197 One-Time <span style="color: #DC2626; font-weight: 800;">(${n} of 50 spots remaining)</span>
      </div>
      <a href="#offer" class="sticky-primary-cta founder-cta-btn">
        Add My Founding Partner Position &rarr;
      </a>
    </div>
  </div>
  `;
}
